import { InputNumber, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import normalizePrice from "@/utils/normalizePrice";
import { amountOptions } from "./config";

interface AmountSectionProps {
  amount: number | null;
  onChange: (amount: number | null) => void;
  minimumAmount?: number;
  error?: string;
}

export default function AmountSection({
  amount,
  onChange,
  minimumAmount,
  error,
}: AmountSectionProps) {
  const { t } = useTranslation();

  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-[#171717] md:gap-2 md:text-base">
          <span className="flex h-3 w-3 items-center justify-center rounded-full bg-[#1E1E1E] text-[8px] text-white md:h-[18px] md:w-[18px] md:text-[11px]">
            1
          </span>
          {t("components.chargeModal.amountSection.title", {
            defaultValue: "Amount",
          })}
        </h3>
        {error && <span className="text-xs text-[#D92D20]">{error}</span>}
      </div>

      <Tooltip
        title={
          minimumAmount
              ? t("components.chargeModal.amountSection.minimumRequired", {
                  amount: normalizePrice(minimumAmount),
                  defaultValue: "A minimum of {amount} is required.",
                })
            : ""
        }
      >
        <div className="relative">
          <InputNumber
            aria-label={t("components.chargeModal.amountSection.customAmount", {
              defaultValue: "Custom amount",
            })}
            prefix={
              <span className="flex h-full items-center text-sm font-normal leading-none text-[#858585]">
                {t("components.chargeModal.amountSection.custom", {
                  defaultValue: "Custom",
                })}
              </span>
            }
            className="h-[50px] w-full pr-7 text-2xl font-semibold [&_.ant-input-number-input-wrap]:h-full [&_.ant-input-number-input]:h-full [&_.ant-input-number-input]:py-0 md:h-10 md:text-base"
            controls={false}
            precision={2}
            value={amount ?? undefined}
            formatter={(value) =>
              value === undefined || value === null
                ? ""
                : Number(value).toLocaleString("en-US")
            }
            parser={(value) => {
              const parsedValue = value?.replace(/,/g, "") ?? "";
              return (parsedValue === ""
                ? undefined
                : Number(parsedValue)) as unknown as number;
            }}
            onChange={(value) => {
              const nextValue = Number(value);
              onChange(
                value === null || !Number.isFinite(nextValue)
                  ? null
                  : nextValue
              );
            }}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-base text-[#171717] md:text-xl md:text-[#A3A3A3]">
            $
          </span>
        </div>
      </Tooltip>

      <div className="mt-2 grid grid-cols-3 gap-1.5 md:mt-3 md:gap-2">
        {amountOptions.map((option) => {
          const selected = amount === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onChange(option)}
              className={`h-[35px] rounded-md border text-sm font-semibold transition-colors md:h-[45px] md:rounded-lg ${
                selected
                  ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                  : "border-[#D8D8D8] bg-white text-[#171717] hover:border-[#8A8A8A]"
              }`}
              style={{
                boxShadow: selected
                  ? "0 1px 2px rgba(10, 13, 18, .05), inset 0 -2px 0 rgba(10, 13, 18, .08), inset 0 0 0 1px rgba(10, 13, 18, .18)"
                  : "0 1px 2px rgba(10, 13, 18, .05)",
              }}
            >
              {normalizePrice(option)}
            </button>
          );
        })}
      </div>
    </section>
  );
}
